'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { FlixmediaFragmentDoc } from '@packages/gql/generated/shopping/FlixmediaFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useRef, useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import { useManufacturerTracking } from '../Tracking/hooks/useManufacturerTracking/useManufacturerTracking';

/* GraphQL */ `
  fragment Flixmedia on Product {
    ean
    brand {
      image
      name
    }
    flixmedia(language: $lang) {
      contentUrl
    }
  }
`;

export type FlixmediaProps = {
  productData: FragmentType<typeof FlixmediaFragmentDoc>;
};

/**
 * Flixmedia component description displayed in storybook
 * */
export const Flixmedia: FC<FlixmediaProps> = ({ productData }) => {
  const { flixmedia, brand } = unmask<typeof FlixmediaFragmentDoc>(productData);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);

  const { trackGlycerinButtonClick, trackGlycerinDisplayOverlay } = useManufacturerTracking({
    brandName: brand?.name || '',
    ref: cardRef,
    vendor: 'Flixmedia',
  });

  const onClickHandler = () => {
    setIsDialogOpen(true);
    trackGlycerinButtonClick();
    trackGlycerinDisplayOverlay();
  };

  if (!flixmedia) {
    return null;
  }

  return (
    <div>
      <Card ref={cardRef}>
        <CardContent
          className="flex cursor-pointer items-center gap-1.5 p-1.5"
          onClick={onClickHandler}
        >
          {brand?.image && (
            <Image
              loading="lazy"
              src={brand.image}
              alt={brand.name}
              style={{ objectFit: 'contain' }}
              width={50}
              height={30}
            />
          )}
          <span>
            {formatMessage(
              {
                id: 'flixmedia.card.title',
                defaultMessage: 'Weitere Herstellerinformationen von {brand}',
              },
              {
                brand: brand?.name || '',
              },
            )}
          </span>
        </CardContent>
      </Card>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        className="h-full w-full md:h-[65%]"
      >
        <DialogHeader onClose={() => setIsDialogOpen(false)}>
          {formatMessage(
            {
              id: 'flixmedia.modal.title',
              defaultMessage: 'Herstellerinformationen von {brand}',
            },
            {
              brand: brand?.name || '',
            },
          )}
        </DialogHeader>
        <DialogContent className="px-1">
          <iframe title="title" src={flixmedia?.contentUrl} className="h-full w-full" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
